<template>
  <div class="fixed inset-0 z-50 bg-white flex flex-col overflow-hidden h-full max-h-full">
    <!-- Payment Dialog Bottomsheet -->
    <PackagePaymentDialog
      ref="paymentDialog"
      :totalAmount="totalAmount"
      :deliveryCost="deliveryCost"
      :paymentFee="paymentFee"
      :escrowEnabled="results.completeData?.paymentLinkEnabled || false"
      @paymentConfirmed="onPaymentConfirmed"
      @cancelled="onPaymentCancelled"
    />
    <!-- Header -->
    <!-- Header -->
    <div class="p-4 pb-12 bg-black flex-shrink-0">
      <div class="mobile-status-padding"></div>
      <div class="grid grid-cols-12 items-center justify-center">
        <div class="col-span-3 flex items-center">
          <div class="w-6 h-6 cursor-pointer" @click="goBack">
            <svg
              class="w-full h-full text-white -rotate-90"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M5 15l7-7 7 7"
              />
            </svg>
          </div>
        </div>
        <div class="col-span-6 text-white text-2xl font-bold text-center">
          Livraison
        </div>
        <div class="col-span-3 flex justify-end gap-2">
          <div
            class="w-2 h-2 rounded-full"
            :class="{
              'bg-white': step >= markerStep,
              'bg-gray-500': step < markerStep
            }"
            v-for="markerStep in steps"
            :key="markerStep"
          ></div>
        </div>
      </div>
      <div class="text-center text-primary-500">
        <span v-if="step > steps">Finalisation</span>
        <span v-else>{{ step }} sur {{ steps }}</span>
      </div>
    </div>

    <!-- Content -->
    <div class="flex-1 min-h-0 overflow-hidden relative">
      <div class="absolute -top-8 inset-0 bg-white rounded-t-3xl flex flex-col pt-8">
        <!-- Scrollable content area -->
        <div class="flex-1 overflow-auto pb-4">
        <component
          :is="currentStepComponent"
          ref="stepComponent"
          :filledData="results"
          v-if="currentStepComponent"
        />
      </div>

      <!-- Navigation Buttons -->
      <div class="p-6 flex flex-col gap-4 border-t border-gray-200 flex-shrink-0">
        <div class="flex gap-4">
        <button
          @click="step--"
          v-if="step !== 1"
          class="flex-1 px-6 py-4 bg-white border-2 border-primary-500 text-primary-500 rounded-xl font-bold hover:bg-gray-50 transition"
        >
          Précédent
        </button>
        <button
          class="buttonComponent w-full text-white rounded-xl font-bold hover:bg-primary-600 transition"
          @click="validateStep"
        >
          {{ step == steps ? 'Lancer la livraison' : 'Continuer' }}
        </button>
        </div>
        <div class="mobile-bottom-padding h-2"></div>
      </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, defineAsyncComponent, onUnmounted, onMounted } from 'vue';
import { bus } from '@core/events/eventSystem';
import constants from '@core/utils/constants';
import PackagePaymentDialog from './PackagePaymentDialog.bottomsheet.vue';

const props = defineProps({
  orderData: {
    type: Object,
    default() {
      return {};
    }
  },
  draftData: {
    type: Object,
    default() {
      return null;
    }
  }
});

const emit = defineEmits(['close', 'complete']);

const step = ref(1);
const steps = ref(5);
const stepComponent = ref(null);
const results = ref({});
const loadingFees = ref(false);
const feesCalculated = ref(false);
const paymentDialog = ref(null);
const draftId = ref(null);

// Computed properties for payment dialog
const totalAmount = computed(() => {
  const complete = results.value?.completeData;
  // if (complete?.paymentLinkEnabled && complete?.deliveryFeePayer === 'receiver') {
  //   return complete?.senderToPay ?? 0;
  // }
  return results.value?.fees?.totalAmount;
});
const deliveryCost = computed(() => {
  // const complete = results.value?.completeData;
  // if (complete?.paymentLinkEnabled && complete?.deliveryFeePayer === 'receiver') {
  //   return 0;
  // }
  // return results.value?.fees?.deliveryCost || results.value?.fees?.deliveryFee || 0;
  return results.value?.fees?.customerDeliveryCost || 0;
});
const paymentFee = computed(() => results.value?.fees?.paymentFee || results.value?.fees?.serviceFee || 0);

function onFeesCalculated(feesData) {
  console.log('[Wizard] FEES_CALCULATED event received:', feesData);
  results.value.fees = feesData;
  loadingFees.value = false;
  feesCalculated.value = true;
  // Move to next step only if we are currently at DeliveryTypeStep (step 4)
  if (step.value === 4) {
    console.log('[Wizard] Moving to step', step.value + 1);
    step.value++;
  }
  saveDraft();
}

function onFeesCalculateError(error) {
  console.error('[Wizard] Fees calculation error:', error);
  loadingFees.value = false;
}

function onDeliveryCreated(deliveryData) {
  console.log('[Wizard] Delivery created:', deliveryData);
  clearDraft();
  // Note: Don't close wizard here - let PackageCreatedSuccessDialog handle the success display
  // The wizard will be closed after the success dialog is shown
  setTimeout(() => {
    emit('close');
  }, 500);
}

function onDeliveryCreateError(error) {
  console.error('[Wizard] Delivery creation error:', error);
}

function onWizardClose() {
  emit('close');
}

onMounted(() => {
  bus.on(constants.EVENTS.PACKAGES.FEES_CALCULATED, onFeesCalculated);
  bus.on(constants.EVENTS.PACKAGES.FEES_CALCULATE_ERROR, onFeesCalculateError);
  bus.on(constants.EVENTS.PACKAGES.DELIVERY_CREATED, onDeliveryCreated);
  bus.on(constants.EVENTS.PACKAGES.DELIVERY_CREATE_ERROR, onDeliveryCreateError);
  bus.on(constants.EVENTS.PACKAGES.WIZARD_CLOSE, onWizardClose);

  if (props.draftData) {
    initializeFromDraft(props.draftData);
  }
});

onUnmounted(() => {
  bus.off(constants.EVENTS.PACKAGES.FEES_CALCULATED, onFeesCalculated);
  bus.off(constants.EVENTS.PACKAGES.FEES_CALCULATE_ERROR, onFeesCalculateError);
  bus.off(constants.EVENTS.PACKAGES.DELIVERY_CREATED, onDeliveryCreated);
  bus.off(constants.EVENTS.PACKAGES.DELIVERY_CREATE_ERROR, onDeliveryCreateError);
  bus.off(constants.EVENTS.PACKAGES.WIZARD_CLOSE, onWizardClose);
});

function initializeFromDraft(draft) {
  if (!draft) return;
  draftId.value = draft.id;
  // step.value = draft.step || 1;
  results.value = draft.results || {};
}

function saveDraft() {
  if (step.value === 1 && !results.value[stepIds[1]]) return;

  if (!draftId.value) {
    draftId.value = 'draft_' + Date.now();
  }

  const draftsJson = localStorage.getItem('package_drafts');
  let draftsMap = {};
  if (draftsJson) {
    try {
      draftsMap = JSON.parse(draftsJson);
    } catch (e) {
      console.error(e);
    }
  }

  draftsMap[draftId.value] = {
    id: draftId.value,
    step: step.value,
    results: JSON.parse(JSON.stringify(results.value)),
    updatedAt: Date.now(),
    createdAt: draftsMap[draftId.value]?.createdAt || Date.now(),
    status: 'drafts',
    srcDstLocation: results.value.srcDstLocation || null,
  };

  localStorage.setItem('package_drafts', JSON.stringify(draftsMap));
}

function clearDraft() {
  if (!draftId.value) return;
  const draftsJson = localStorage.getItem('package_drafts');
  if (draftsJson) {
    try {
      const draftsMap = JSON.parse(draftsJson);
      delete draftsMap[draftId.value];
      localStorage.setItem('package_drafts', JSON.stringify(draftsMap));
    } catch (e) {
      console.error(e);
    }
  }
  draftId.value = null;
}

/**
 * Calculate delivery fees by emitting event (controller handles API call)
 */
function calculateDeliveryFees() {
  const srcDstLocation = results.value.srcDstLocation;
  const packageType = results.value.packageType;
  const deliveryType = results.value.deliveryType;

  if (!srcDstLocation?.srcPosition || !srcDstLocation?.dstPosition) {
    bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
      title: 'Erreur',
      body: "Positions de départ et d'arrivée requises",
      type: 'error'
    });
    return false;
  }

  loadingFees.value = true;
  feesCalculated.value = false;

  // Emit event to controller - it will handle API call
  bus.emit(constants.EVENTS.PACKAGES.FEES_CALCULATE, {
    srcDstLocation,
    packageType,
    deliveryType
  });

  return true;
}

const stepComponents = {
  1: defineAsyncComponent(() => import('./PickDateStep.vue')),
  2: defineAsyncComponent(() => import('./SrcDstPositionStep.vue')),
  3: defineAsyncComponent(() => import('./PackageTypeStep.vue')),
  4: defineAsyncComponent(() => import('./DeliveryTypeStep.vue')),
  5: defineAsyncComponent(() => import('./SummaryStep.vue'))
};

const stepIds = {
  1: 'startDate',
  2: 'srcDstLocation',
  3: 'packageType',
  4: 'deliveryType',
  5: 'summary'
};

/**
 * Get current userId from store or localStorage
 */
function getUserId() {
  // Try to get from user store first
  const userStore = window.$stores?.user;
  if (userStore?.state?.current?.id) {
    return userStore.state.current.id;
  }

  // Fallback to localStorage
  const userId = localStorage.getItem('user_id');
  if (userId) {
    return userId;
  }

  // Generate a temporary userId if none exists
  const tempUserId = 'temp-user-' + Date.now();
  localStorage.setItem('user_id', tempUserId);
  return tempUserId;
}

const currentStepComponent = computed(() => {
  return stepComponents[step.value] || null;
});

/**
 * Validate current step and move to next
 */
async function validateStep() {
  if (step.value == steps.value) {
    await launchPayment();
    return;
  }

  if (!stepComponent.value || !stepComponent.value.getStepResult) {
    bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
      title: 'Erreur',
      body: 'Erreur de validation',
      type: 'error'
    });
    return;
  }

  const result = await stepComponent.value.getStepResult();
  if (!result) return;

  results.value[stepIds[step.value]] = result;

  // After DeliveryTypeStep (step 4), emit event to calculate fees
  if (step.value === 4) {
    saveDraft();
    // calculateDeliveryFees will emit PACKAGES.FEES_CALCULATE event
    // The response will come via PACKAGES.FEES_CALCULATED event which moves to next step
    calculateDeliveryFees();
    return; // Don't increment step - wait for event
  }

  step.value++;
  saveDraft();
}

/**
 * Launch payment dialog
 */
function launchPayment() {
  if (!stepComponent.value || !stepComponent.value.getStepResult) {
    bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
      title: 'Erreur',
      body: 'Erreur de validation',
      type: 'error'
    });
    return;
  }

  const finalData = stepComponent.value.getStepResult();
  if (!finalData) return;

  // Abort if remaining amount is negative
  if (finalData.remainingAmount < 0) {
    bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
      title: 'Erreur',
      body: 'Le reste à recevoir est négatif. Impossible de poursuivre.',
      type: 'error'
    });
    return;
  }

  // Merge all results
  const completeData = {
    ...results.value,
    ...finalData
  };
  console.log('🚀 ~ launchPayment ~ completeData:', completeData);

  // Store complete data for later use after payment
  results.value.completeData = completeData;

  // Open payment dialog instead of creating delivery directly
  paymentDialog.value?.showModal();
}

/**
 * Handle payment confirmed from dialog
 */
function onPaymentConfirmed(paymentData) {
  console.log('[Wizard] Payment confirmed:', paymentData);

  const completeData = results.value.completeData;
  if (!completeData) {
    bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
      title: 'Erreur',
      body: 'Données de livraison manquantes',
      type: 'error'
    });
    return;
  }

  // Emit event to controller - it will handle API call
  bus.emit(constants.EVENTS.PACKAGES.DELIVERY_CREATE, {
    startDate: completeData.startDate,
    srcDstLocation: completeData.srcDstLocation,
    packageType: completeData.packageType,
    deliveryType: completeData.deliveryType,
    fees: completeData.fees,
    paymentMethod: paymentData.paymentMethod,
    paymentOperator: paymentData.operator,
    paymentProvider: paymentData.provider,
    paymentPhone: paymentData.phoneNumber,
    walletId: paymentData.walletId,
    // Escrow / lien de paiement sécurisé
    merchandisePrice: completeData.merchandisePrice || 0,
    deliveryFeePayer: completeData.deliveryFeePayer || 'sender',
    paymentLinkEnabled: completeData.paymentLinkEnabled || false,
  });
}

/**
 * Handle payment cancelled from dialog
 */
function onPaymentCancelled() {
  console.log('[Wizard] Payment cancelled by user');
}

/**
 * Go back to previous step
 */
function goBack() {
  if (step.value == 1) {
    emit('close');
  } else {
    step.value--;
    saveDraft();
  }
}
</script>
